import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { get } from '../lib/api';
import { Avatar, ErrorNote, Spinner } from '../components/ui';
import type { Profile } from '../lib/types';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

export default function Login(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [picked, setPicked] = useState<Profile | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    get<{ profiles: Profile[] }>('/api/auth/profiles')
      .then((res) => setProfiles(res.profiles))
      .catch((err) => setError((err as Error).message));
  }, []);

  async function attempt(candidate: string) {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await login(picked.id, candidate);
      navigate('/', { replace: true }); // always land on the home tab, whoever signed in last
    } catch (err) {
      setError((err as Error).message);
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  function press(key: string) {
    if (busy) return;
    if (key === '⌫') {
      setPin((current) => current.slice(0, -1));
      return;
    }
    if (!key) return;
    const next = (pin + key).slice(0, 8);
    setPin(next);
    if (next.length === 4) void attempt(next); // most PINs are four digits — log in as soon as it fits
  }

  if (!profiles) return <Spinner />;

  if (!picked) {
    return (
      <div className="app no-nav">
        <h1>Who's this?</h1>
        <p className="muted">Tap your name to sign in.</p>
        <ErrorNote error={error} />
        <div className="profile-grid">
          {profiles.map((profile) => (
            <button
              key={profile.id}
              className="profile-btn"
              type="button"
              onClick={() => {
                setPicked(profile);
                setPin('');
                setError(null);
              }}
            >
              <Avatar emoji={profile.emoji} large />
              <span>{profile.name}</span>
              <span className="tiny muted">{profile.role === 'parent' ? 'Parent' : 'Kid'}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="app no-nav center">
      <button className="btn ghost" type="button" onClick={() => setPicked(null)} style={{ float: 'left' }}>
        ‹ Back
      </button>
      <div style={{ clear: 'both', paddingTop: 24 }}>
        <Avatar emoji={picked.emoji} large />
      </div>
      <h1>Hi {picked.name}</h1>
      <p className="muted">Enter your PIN</p>
      <div className="row" style={{ justifyContent: 'center', gap: 12, margin: '18px 0 8px' }}>
        {Array.from({ length: Math.max(4, pin.length) }).map((_, index) => (
          <span
            key={index}
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: index < pin.length ? 'var(--accent)' : 'var(--surface-2)',
              border: '1px solid var(--border)',
            }}
          />
        ))}
      </div>
      <ErrorNote error={error} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
          maxWidth: 300,
          margin: '12px auto 0',
        }}
      >
        {KEYS.map((key, index) => (
          <button
            key={index}
            type="button"
            className="btn secondary"
            style={{ minHeight: 62, fontSize: 22, visibility: key ? 'visible' : 'hidden' }}
            onClick={() => press(key)}
          >
            {key}
          </button>
        ))}
      </div>
      <button
        className="btn block"
        type="button"
        style={{ maxWidth: 300, margin: '14px auto 0' }}
        disabled={pin.length < 4 || busy}
        onClick={() => void attempt(pin)}
      >
        {busy ? 'Checking…' : 'Sign in'}
      </button>
    </div>
  );
}
